/**
 * The narrowest the drawer ever opens at, and the width it opens at where the
 * window cannot be measured.
 *
 * Also the floor of `chatDrawerDefaultWidth`: a small laptop opens at exactly
 * this, and nothing opens narrower.
 */
export const CHAT_DRAWER_WIDTH = 400;

/**
 * How narrow and how wide it may be dragged. The floor is where the composer
 * and a card stop fitting side by side; the ceiling is where a conversation
 * stops being a column beside the page and starts being a second page.
 */
export const CHAT_DRAWER_MIN_WIDTH = 320;
export const CHAT_DRAWER_MAX_WIDTH = 820;

/** How much of the drawer is left showing when it is collapsed. */
export const CHAT_STRIP_WIDTH = 44;

/**
 * How much of the window a drawer nobody has sized takes. A share rather than a
 * number, because the same 400px column that is half a laptop is a strip on a
 * 27" screen — and the conversation is the thing being read, not the sidebar.
 */
const VIEWPORT_SHARE = 0.3;

/**
 * The width the drawer opens at before anyone drags it, at this window.
 *
 * Clamped at both ends: below the floor it stops being usable, and above the
 * ceiling it stops being a column beside the page. `undefined` asks the
 * document, which is also why a caller with no window at all — a test, a render
 * without a DOM — gets the floor rather than a crash.
 *
 * What ends up on screen is still `chatDrawerFit`'s answer, not this: this is
 * only what is asked for.
 */
export function chatDrawerDefaultWidth(viewportWidth?: number): number {
  const measured =
    viewportWidth ??
    (typeof document === 'undefined' ? 0 : document.documentElement.clientWidth);

  return Math.min(
    Math.max(Math.round(measured * VIEWPORT_SHARE), CHAT_DRAWER_WIDTH),
    CHAT_DRAWER_MAX_WIDTH,
  );
}
